import { AwsRum, AwsRumConfig } from "aws-rum-web";
import {
  getCurrentRumStage,
  RumStageProps,
  RUM_APP_ENDPOINT,
  RUM_APP_REGION,
  RUM_APP_TELEMETRIES,
  RUM_APP_VERSION,
} from "./rumConstants";

export const getCurrentProductId = (): string => {
  // Extract product ID from parent page URL
  const url = window.location.href;

  // Look for product_id= parameter
  const productIdMatch = url.match(/product_id=([^&]+)/);
  if (productIdMatch) {
    return decodeURIComponent(productIdMatch[1]);
  }

  return "unknown_product";
};

let rumClient: AwsRum | null = null;

export const rumInit = (allowCookies = false): void => {
  const rumStage: RumStageProps = getCurrentRumStage();
  try {
    const config: AwsRumConfig = {
      ...rumStage.rumStageConfig,
      endpoint: RUM_APP_ENDPOINT,
      telemetries: RUM_APP_TELEMETRIES,
      allowCookies: false, // Disabled to prevent session-based event deduplication
    };

    rumClient = new AwsRum(rumStage.appId, RUM_APP_VERSION, RUM_APP_REGION, config);
  } catch (error) {
    // Ignore errors thrown during CloudWatch RUM web client initialization
  }
};

/**
 * Track successful iframe content loading
 * Monitors: Course content successfully rendered in iframe, performance metrics
 */
export const trackIframeLoad = (startTime: number) => {
  if (!rumClient) return;
  const loadTime = Date.now() - startTime;

  rumClient.recordEvent("iframe_content_load_success", {
    name: "iframe_content_load_success",
    timestamp: Date.now(),
  });

  rumClient.recordEvent("course_content_render_time", {
    name: "course_content_render_time",
    duration: loadTime,
    timestamp: Date.now(),
  });
};

/**
 * Track iframe content loading failures
 * Monitors: Network failures, 404s, CORS issues, authentication failures
 */
export const trackIframeError = () => {
  if (!rumClient) return;

  // Emit error event for iframe load
  rumClient.recordEvent("iframe_content_load_error", {
    name: "iframe_content_load_error",
    timestamp: Date.now(),
    error: true,
  });
};

/**
 * Track monitoring events to CloudWatch RUM
 * Centralized method for all monitoring event tracking
 */
export const trackMonitoringEvent = (eventName: string, details: Record<string, any>): void => {
  if (rumClient) {
    rumClient.recordEvent(eventName, {
      name: eventName,
      ...details,
    });
  }
};

// Enhanced RUM client with comprehensive monitoring
export const enhancedRumClient = {
  /**
   * Record Rise course player JavaScript errors
   * Monitors: TypeError, ReferenceError, SyntaxError, RangeError, EvalError
   * Rise-specific: TinCan API errors, SCORM communication failures, course initialization errors
   * Articulate Rise: Player loading failures, content parsing errors, navigation errors
   */
  recordCourseError: (errorData: {
    type?: string;
    message: string;
    productId?: string;
    timestamp: number;
    stack?: string;
    filename?: string;
    lineno?: number;
  }) => {
    if (!rumClient) return;

    rumClient.recordEvent("course_player_error", {
      name: "course_player_error",
      timestamp: errorData.timestamp,
      error: true,
      errorType: errorData.type || "unknown",
      errorMessage: errorData.message,
      productId: errorData.productId || getCurrentProductId(),
      stack: errorData.stack || undefined,
      filename: errorData.filename || "unknown",
      lineno: errorData.lineno || 0,
    });
  },

  /**
   * Record course performance and interaction metrics
   * Monitors: Course load times, user interaction delays, content rendering performance
   * Rise-specific: Slide transition times, media loading delays, assessment response times
   * Operational: Slow course launches, timeout issues, performance degradation
   */
  recordPerformanceMetric: (metricData: {
    loadTime?: number;
    interactionTime?: number;
    productId?: string;
    metricType?: string;
  }) => {
    if (!rumClient) return;

    rumClient.recordEvent("course_performance", {
      name: "course_performance",
      loadTime: metricData.loadTime || 0,
      interactionTime: metricData.interactionTime || 0,
      productId: metricData.productId || getCurrentProductId(),
      metricType: metricData.metricType || "general",
      timestamp: Date.now(),
    });
  },

  /**
   * Record xAPI/TinCan communication events and failures
   * Monitors: LRS connection failures, statement posting errors, authentication issues
   * xAPI-specific: Invalid statement format, verb/object errors, context data issues
   * Network: 404 LRS endpoints, timeout errors, CORS failures, certificate issues
   * Rise Integration: Progress tracking failures, completion status errors, score reporting issues
   */
  recordXAPIEvent: (xapiData: {
    eventType: string;
    success: boolean;
    url?: string;
    status?: number;
    error?: string;
  }) => {
    if (!rumClient) return;

    rumClient.recordEvent("xapi_communication", {
      name: "xapi_communication",
      eventType: xapiData.eventType,
      success: xapiData.success,
      url: xapiData.url || undefined,
      status: xapiData.status,
      error: xapiData.error || undefined,
      productId: getCurrentProductId(),
      timestamp: Date.now(),
    });
  },
};
